// src/pages/academy/AcademySkillDetail.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  BookOpen,
  Pencil,
  Trash2,
  Loader2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  FileText,
  Headphones,
  Video,
  Check,
} from "lucide-react";
import {
  academySkillsAPI,
  academyQuestionsAPI,
  academyContentAPI,
  getApiError,
} from "../../services/academyService";
import {
  ACADEMY_ROOT,
  SKILL_TYPE_LABELS,
  CONTENT_TYPES,
  FLAT_CONTENT_TYPES,
  PARENT_CONTENT_TYPES,
  CONTENT_KIND,
  CHILD_CONTENT_KIND,
} from "./academyConstants";
import AcademyDeleteModal from "./components/AcademyDeleteModal";
import RichText, { stripHtml } from "./components/RichText";

// المهارة نوعها MIXED → كل الأنواع متاحة، وإلا نوع واحد بس.
const typesForSkill = (skillType) =>
  skillType === "MIXED" ? CONTENT_TYPES : [skillType];

// الباك إند بيقص page_size عند 100 (maximum=100 في _int_param)،
// فلازم نطلب 100 بالظبط عشان مفيش عناصر تختفي بدون خطأ.
const PAGE_SIZE = 100;

// أنواع المحتوى المسطّحة اللي شكلها MCQ (اختيارات + إجابة صح) — Writing مسطّحة برضه بس مش MCQ.
const FLAT_MCQ_TYPES = ["VOCABULARY", "GRAMMAR"];

export default function AcademySkillDetail() {
  const navigate = useNavigate();
  const { skillId } = useParams();

  const [skill, setSkill] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const restoreKey = `academy-skill-restore-${skillId}`;
  const [expandedParents, setExpandedParents] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(restoreKey));
      return new Set(saved?.expanded || []);
    } catch {
      return new Set();
    }
  });
  const [deleteModal, setDeleteModal] = useState(null); // { kind, id, label }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  useEffect(() => {
    if (loading) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(restoreKey));
    } catch {}
    if (!saved?.anchor) return;
    sessionStorage.removeItem(restoreKey);

    // بنحاول كذا مرة عشان لو الـ layout أو الميديا غيّروا السكرول
    [0, 150, 400, 800].forEach((ms) =>
      setTimeout(() => {
        const el = document.getElementById(saved.anchor);
        if (!el) console.warn("[restore] element not found:", saved.anchor);
        el?.scrollIntoView({ block: "center" });
      }, ms)
    );
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");
      const [skillData, questionsData] = await Promise.all([
        academySkillsAPI.getById(skillId),
        academyQuestionsAPI.getSkillQuestions(skillId, 1, PAGE_SIZE),
      ]);
      setSkill(skillData);
      setEntries(questionsData.questions || []);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "تعذر تحميل محتوى المهارة"));
    } finally {
      setLoading(false);
    }
  };

  const entriesByType = useMemo(() => {
    const map = {};
    for (const type of CONTENT_TYPES) map[type] = [];
    for (const entry of entries) {
      if (map[entry.type]) map[entry.type].push(entry);
    }
    return map;
  }, [entries]);

  const toggleParent = (id) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const askDelete = (kind, id, label) => {
    setDeleteError("");
    setDeleteModal({ kind, id, label: stripHtml(label) });
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      setDeleting(true);
      setDeleteError("");
      await academyContentAPI.remove(deleteModal.kind, deleteModal.id);
      setDeleteModal(null);
      fetchAll();
    } catch (err) {
      console.error(err);
      setDeleteError(getApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  // الرجوع لليست مهارات الامتحان (academy/exams/:examId/skills)
  // لو مفيش examId في الـ response بنرجع لصفحة الأكاديمي الرئيسية
  const backToList = () => {
    const raw = skill?.exam ?? skill?.exam_id;
    const examId = raw && typeof raw === "object" ? raw.id : raw;
    navigate(examId ? `${ACADEMY_ROOT}/exams/${examId}/skills` : ACADEMY_ROOT);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const availableTypes = skill ? typesForSkill(skill.skill_type) : [];
  const isMixedSkill = skill?.skill_type === "MIXED";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={backToList}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-indigo-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{skill?.title}</h1>
          <p className="text-sm text-gray-500">
            {skill?.exam_title} · {entries.length} عنصر
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchAll} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* قسم لكل نوع محتوى متاح جوه المهارة */}
      {availableTypes.map((type) => (
        <ContentTypeSection
          key={type}
          type={type}
          collapsible={isMixedSkill}
          items={entriesByType[type] || []}
          expandedParents={expandedParents}
          onToggleParent={toggleParent}
          onEdit={(kind, item, anchor) => {
            try {
              sessionStorage.setItem(
                restoreKey,
                JSON.stringify({ anchor, expanded: [...expandedParents] })
              );
            } catch {}
            navigate(
              `${ACADEMY_ROOT}/skills/${skillId}/questions/${kind}/${item.id}/edit`,
              { state: { item } }
            );
          }}
          onDeleteFlat={(id, label) => askDelete(CONTENT_KIND[type], id, label)}
          onDeleteChild={(id, label) =>
            askDelete(CHILD_CONTENT_KIND[type], id, label)
          }
          onDeleteParent={(id, label) =>
            askDelete(CONTENT_KIND[type], id, label)
          }
          onAddFlat={() =>
            navigate(
              `${ACADEMY_ROOT}/skills/${skillId}/questions/${CONTENT_KIND[type]}/create`
            )
          }
          onAddParent={() =>
            navigate(
              `${ACADEMY_ROOT}/skills/${skillId}/${type.toLowerCase()}/create`
            )
          }
          onAddChild={(parentId) =>
            navigate(
              `${ACADEMY_ROOT}/skills/${skillId}/questions/${CHILD_CONTENT_KIND[type]}/create?parent_id=${parentId}`
            )
          }
        />
      ))}

      {deleteModal && (
        <AcademyDeleteModal
          title="حذف العنصر"
          itemName={deleteModal.label}
          warning="لو ده عنصر أب (قطعة/تسجيل/فيديو) هيتحذف معاه كل الأسئلة التابعة له."
          deleting={deleting}
          error={deleteError}
          onCancel={() => setDeleteModal(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

// ============================================
// قسم نوع واحد (Vocabulary / Grammar / Writing / Reading / Listening / Speaking)
// ============================================
function ContentTypeSection({
  type,
  items,
  expandedParents,
  onToggleParent,
  onEdit,
  onDeleteFlat,
  onDeleteChild,
  onDeleteParent,
  onAddFlat,
  onAddParent,
  onAddChild,
  collapsible = false,
}) {
  // القسم مفتوح افتراضيًا، والسهم (في المختلط بس) بيقفله ويفتحه
  const [collapsed, setCollapsed] = useState(false);

  const typeInfo = SKILL_TYPE_LABELS[type] || {
    label: type,
    color: "bg-gray-100 text-gray-700",
  };
  const isParentType = PARENT_CONTENT_TYPES.includes(type);
  const isFlatType = FLAT_CONTENT_TYPES.includes(type);
  const isMcqType = FLAT_MCQ_TYPES.includes(type);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            title={collapsed ? "إظهار الأسئلة" : "إخفاء الأسئلة"}
            className="flex items-center gap-2 flex-1 text-left"
          >
            {collapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeInfo.color}`}
            >
              {typeInfo.label}
            </span>
            <span className="text-sm text-gray-400">{items.length} عنصر</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeInfo.color}`}
            >
              {typeInfo.label}
            </span>
            <span className="text-sm text-gray-400">{items.length} عنصر</span>
          </div>
        )}
        {(isFlatType || isParentType) && (
          <button
            onClick={isParentType ? onAddParent : onAddFlat}
            className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {isParentType ? `Add ${typeInfo.label}` : "Add Question"}
          </button>
        )}
      </div>

      {!collapsed &&
        (items.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-gray-400">
            لا يوجد محتوى بعد
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((item) => {
              if (isParentType) {
                return (
                  <ParentRow
                    key={item.id}
                    domId={`entry-${type}-${item.id}`}
                    type={type}
                    item={item}
                    expanded={expandedParents.has(item.id)}
                    onToggle={() => onToggleParent(item.id)}
                    onEditParent={() => onEdit(mapKind(type), item, `entry-${type}-${item.id}`)}
                    onDeleteParent={() => onDeleteParent(item.id, item.title)}
                    onEditChild={(child) =>
                      onEdit(
                        mapChildKind(type),
                        child,
                        `entry-${type}-${item.id}-child-${child.id}`
                      )
                    }
                    onDeleteChild={(childId, label) =>
                      onDeleteChild(childId, label)
                    }
                    onAddChild={() => onAddChild(item.id)}
                  />
                );
              }

              if (isMcqType) {
                return (
                  <McqRow
                    key={item.id}
                    domId={`entry-${type}-${item.id}`}
                    item={item}
                    onEdit={() => onEdit(mapKind(type), item, `entry-${type}-${item.id}`)}
                    onDelete={() => onDeleteFlat(item.id, item.question_text)}
                  />
                );
              }

              return (
                <FlatRow
                  key={item.id}
                  domId={`entry-${type}-${item.id}`}
                  type={type}
                  item={item}
                  onEdit={() => onEdit(mapKind(type), item, `entry-${type}-${item.id}`)}
                  onDelete={() =>
                    onDeleteFlat(
                      item.id,
                      type === "WRITING" ? item.title : item.question_text
                    )
                  }
                />
              );
            })}
          </div>
        ))}
    </div>
  );
}

function mapKind(type) {
  return CONTENT_KIND[type];
}
function mapChildKind(type) {
  return CHILD_CONTENT_KIND[type];
}

// صف "مسطّح" بسيط (Writing — مفيهوش اختيارات، بس عنوان)
function FlatRow({ type, item, onEdit, onDelete, domId }) {
  const title = stripHtml(type === "WRITING" ? item.title : item.question_text);
  return (
    <div id={domId} className="flex items-start justify-between gap-3 px-5 py-4">
      <div className="min-w-0 flex-1 text-right">
        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {item.difficulty} · {item.points} نقطة
          {!item.is_active && " · Inactive"}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const DIFFICULTY_STYLES = {
  EASY: "bg-emerald-50 text-emerald-700",
  MEDIUM: "bg-amber-50 text-amber-700",
  HARD: "bg-rose-50 text-rose-700",
};

const CHOICE_FIELDS = [
  { key: "choice_a", letter: "A" },
  { key: "choice_b", letter: "B" },
  { key: "choice_c", letter: "C" },
  { key: "choice_d", letter: "D" },
];

// كارت كامل لسؤال MCQ (Vocabulary / Grammar / أسئلة Reading-Listening-Speaking التابعة):
// نص السؤال + الاختيارات + الإجابة الصح + الشرح (كله بيتعرض بشكل الـ rich text)
function McqRow({ item, onEdit, onDelete, compact = false, domId }) {
  const choices = CHOICE_FIELDS.map(({ key, letter }) => ({
    letter,
    text: item[key],
  })).filter((c) => c.text);

  return (
    <div id={domId} dir="ltr" className={compact ? "text-left" : "px-5 py-4 text-left"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 text-left">
          <RichText
            html={item.question_text}
            className="text-sm font-medium text-gray-900 leading-relaxed"
          />
          {item.question_image && (
            <img
              src={item.question_image}
              alt=""
              className="mt-2 max-h-32 rounded-lg border border-gray-100 object-contain"
            />
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {choices.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {choices.map((c) => {
            const isCorrect = c.letter === item.correct_answer;
            return (
              <div
                key={c.letter}
                className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border text-left ${
                  isCorrect
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800 font-medium"
                    : "bg-gray-50 border-gray-100 text-gray-600"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                    isCorrect
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {c.letter}
                </span>
                <RichText html={c.text} className="min-w-0 flex-1 break-words" />
                {isCorrect && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      )}

      {(item.explanation || item.english_explanation) && (
        <div className="mt-2.5 space-y-2">
          {item.explanation && (
            <div className="text-xs text-gray-500 text-left leading-relaxed">
              <span className="font-medium text-gray-600">الشرح:</span>
              <RichText html={item.explanation} />
            </div>
          )}
          {item.english_explanation && (
            <div className="text-xs text-gray-500 text-left leading-relaxed">
              <span className="font-medium text-gray-600">Explanation:</span>
              <RichText html={item.english_explanation} />
            </div>
          )}
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-start gap-2 text-xs">
        <span
          className={`px-2 py-0.5 rounded-full font-medium ${
            DIFFICULTY_STYLES[item.difficulty] || "bg-gray-100 text-gray-600"
          }`}
        >
          {item.difficulty}
        </span>
        <span className="text-gray-400">{item.points} نقطة</span>
        {!item.is_active && (
          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-medium">
            Inactive
          </span>
        )}
      </div>
    </div>
  );
}

const PARENT_ICON = { READING: FileText, LISTENING: Headphones, SPEAKING: Video };

// بياخد رابط الميديا مع مراعاة إن الباك إند ممكن يرجّع الحقل الخام (audio_file/video_file)
// أو نسخة الـ URL المحسوبة (audio_file_url/video_file_url) — أيهما موجود.
function getParentMediaUrl(type, item) {
  if (type === "LISTENING") return item.audio_file_url || item.audio_file || null;
  if (type === "SPEAKING") return item.video_file_url || item.video_file || null;
  return null;
}

// صف "أب" (قطعة قراءة / تسجيل صوتي / فيديو) مع الأسئلة التابعة له، قابل للطي
function ParentRow({
  type,
  item,
  domId,
  expanded,
  onToggle,
  onEditParent,
  onDeleteParent,
  onEditChild,
  onDeleteChild,
  onAddChild,
}) {
  const Icon = PARENT_ICON[type] || FileText;
  const mediaUrl = getParentMediaUrl(type, item);

  return (
    <div id={domId}>
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 min-w-0 flex-1 text-right"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 truncate">
            {item.title}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0">
            ({(item.questions || []).length} سؤال)
          </span>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onAddChild}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            aria-label="Add question"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={onEditParent}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onDeleteParent}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ─── ميديا اللستنينج (صوت + نص مكتوب) — دايمًا ظاهرة ─── */}
      {type === "LISTENING" && (mediaUrl || item.transcript) && (
        <div className="px-8 pb-4 space-y-3">
          {mediaUrl && (
            <audio controls src={mediaUrl} className="w-full h-10" preload="none" />
          )}
          {item.transcript && (
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-700 mb-1 text-xs">النص المكتوب</p>
              <RichText html={item.transcript} />
            </div>
          )}
        </div>
      )}

      {/* ─── ميديا السبيكنج (فيديو) — دايمًا ظاهرة ─── */}
      {type === "SPEAKING" && mediaUrl && (
        <div className="px-8 pb-4">
          <video
            controls
            src={mediaUrl}
            poster={item.thumbnail_url || item.thumbnail || undefined}
            className="w-full max-h-72 rounded-xl bg-black"
            preload="none"
          />
        </div>
      )}

      {expanded && (
        <div className="bg-gray-50 divide-y divide-gray-100 border-t border-gray-100">
          {(item.questions || []).length === 0 ? (
            <div className="px-8 py-3 text-xs text-gray-400">لا يوجد أسئلة بعد</div>
          ) : (
            item.questions.map((q) => (
              <div key={q.id} id={`${domId}-child-${q.id}`} className="px-8 py-3">
                <McqRow
                  item={q}
                  onEdit={() => onEditChild(q)}
                  onDelete={() => onDeleteChild(q.id, q.question_text)}
                  compact
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}